import React, { ReactNode, useEffect, useState } from 'react'
import {
  StyleSheet,
  TouchableOpacity,
  View,
  ScrollView,
  Dimensions,
  Keyboard,
  Platform,
} from 'react-native'
import Modal from 'react-native-modal'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { Icon, IconName, Text } from '../atoms'
import { SearchBar } from './SearchBar'
import { useScreen } from '@hooks'
import { Button } from './Button'
import { getCommonStyles } from '@utils'
import { useLanguage } from '@context'

export type OptionsBottomModal = {
  title: string
  iconName: IconName
  hasBackground?: boolean
  onPress: () => void
}

type BottomModalBaseProps = {
  isVisible: boolean
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>
  children: ReactNode
  fitParent?: boolean
  onClose?: () => void
  largePaddingTop?: boolean
  hasCloseButton?: boolean
  wrapContent?: boolean
  /** On mobile, make the modal panel fill the screen (no gap at top/bottom) */
  fullScreen?: boolean
  /** On mobile, cap the sheet at this share of the screen height (0 to 1): the page stays visible above it, and a tap there closes the sheet */
  maxHeightRatio?: number
  /** Desktop, fitParent: label of the bottom button (default « Valider ») */
  validateTitle?: string
}

type BottomModalProps = {
  isVisible: boolean
  options: OptionsBottomModal[]
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>
  search?: boolean
  isMultiple?: boolean
  /** On mobile, make the modal panel fill the screen */
  fullScreen?: boolean
}

export const BottomModalBase = ({
  isVisible,
  setIsVisible,
  children,
  fitParent = false,
  onClose,
  largePaddingTop = true,
  hasCloseButton = false,
  wrapContent = false,
  fullScreen = false,
  maxHeightRatio,
  validateTitle,
}: BottomModalBaseProps): React.ReactElement => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyle(theme, fullScreen)
  const commonStyles = getCommonStyles(theme)
  const insets = useSafeAreaInsets()
  const [keyboardHeight, setKeyboardHeight] = useState(0)
  const { isMobile } = useScreen()

  useEffect(() => {
    const onShow = Keyboard.addListener('keyboardDidShow', (e) => {
      setKeyboardHeight(e.endCoordinates.height)
    })
    const onHide = Keyboard.addListener('keyboardDidHide', () => {
      setKeyboardHeight(0)
    })

    return () => {
      onShow.remove()
      onHide.remove()
    }
  }, [])

  const windowHeight = Dimensions.get('window').height
  const maxHeight = fullScreen
    ? windowHeight - keyboardHeight
    : Math.min(windowHeight - 10 - keyboardHeight, windowHeight * 0.8)

  // On mobile the sheet stops below the status bar (top safe-area inset) and
  // does not react to the keyboard.
  const mobileMaxHeight = maxHeightRatio
    ? Math.min(windowHeight - insets.top, Math.round(windowHeight * maxHeightRatio))
    : windowHeight - insets.top

  const handleClose = () => {
    setIsVisible(false)
    onClose?.()
  }

  // For large screen, use a different modal approach that constrains to parent
  if (!isMobile) {
    return fitParent ? (
      isVisible ? (
        <View
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            width: '100%',
            height: '100%',
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            paddingBottom: 0,
            paddingTop: largePaddingTop ? theme.spacing.xxxl * 2 : theme.spacing.xxxl,
            backgroundColor: theme.colors.background,
            paddingHorizontal: theme.spacing.md,
          }}
        >
          {hasCloseButton && (
            <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
              <Icon name="cross" size={30} color={theme.colors.secondary} />
            </TouchableOpacity>
          )}
          <View style={commonStyles.flex}>{children}</View>
          <View style={[commonStyles.paddingBottom, commonStyles.alignSelfEnd]}>
            <Button
              title={validateTitle ?? t('common.validate')}
              isSmall
              onPress={handleClose}
              style={{ paddingHorizontal: theme.spacing.xxxl }}
            />
          </View>
        </View>
      ) : (
        <></>
      )
    ) : (
      <Modal
        style={styles.modalDesktop}
        isVisible={isVisible}
        onBackdropPress={handleClose}
        onBackButtonPress={handleClose}
        useNativeDriverForBackdrop
        avoidKeyboard
        statusBarTranslucent
      >
        <View
          style={[
            styles.containerNonMobile,
            {
              paddingTop: largePaddingTop ? theme.spacing.xxxl * 2 : theme.spacing.xxxl,
              maxHeight,
            },
          ]}
        >
          <View style={styles.indicator} />
          {hasCloseButton && (
            <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
              <Icon name="cross" size={30} color={theme.colors.secondary} />
            </TouchableOpacity>
          )}
          {children}
        </View>
      </Modal>
    )
  }

  // For mobile, use the original full-screen modal
  return (
    <Modal
      style={styles.modal}
      isVisible={isVisible}
      onBackdropPress={handleClose}
      // Android: « back » closes the sheet (it used to do nothing while one was open).
      onBackButtonPress={handleClose}
      onSwipeComplete={handleClose}
      useNativeDriverForBackdrop
      statusBarTranslucent
      swipeDirection={['down']}
      propagateSwipe={true}
      avoidKeyboard
      deviceHeight={windowHeight}
    >
      <View
        style={[
          styles.container,
          wrapContent && { flex: undefined, paddingBottom: theme.spacing.lg },
          // RN-Web skips `flex: undefined`: without this the sheet still fills
          // the screen in a phone browser.
          wrapContent && Platform.OS === 'web' && styles.wrapContentWeb,
          {
            paddingTop: largePaddingTop ? theme.spacing.xxxl * 2 : theme.spacing.xxxl,
            maxHeight: fullScreen ? undefined : mobileMaxHeight,
          },
          fullScreen && styles.containerFullScreen,
        ]}
      >
        <View style={styles.indicator} />
        {hasCloseButton && (
          <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
            <Icon name="cross" size={30} color={theme.colors.secondary} />
          </TouchableOpacity>
        )}
        {children}
      </View>
    </Modal>
  )
}

export const BottomModal = ({
  isVisible,
  options,
  setIsVisible,
  search,
  isMultiple,
}: BottomModalProps): React.ReactElement => {
  const theme = useTheme()
  const styles = getStyle(theme)
  const { isMobile } = useScreen()
  const [textFilter, setTextFilter] = useState('')

  const textFilterLowerCase = textFilter.toLowerCase()
  const filteredOptions = options.filter((option) =>
    option.title.toLowerCase().includes(textFilterLowerCase),
  )

  return (
    <BottomModalBase isVisible={isVisible} setIsVisible={setIsVisible} wrapContent>
      <View style={[styles.container2, !isMobile && { width: 500 }]}>
        {Boolean(search) && (
          <SearchBar
            onChangeText={(filter) => {
              setTextFilter(filter)
            }}
          />
        )}
        <ScrollView style={styles.options} contentContainerStyle={styles.optionsContent}>
          {filteredOptions.map(({ title, hasBackground, iconName, onPress }, index) => (
            <TouchableOpacity
              key={index}
              onPress={onPress}
              style={[styles.option, hasBackground && { backgroundColor: theme.colors.primary }]}
            >
              <Icon
                name={iconName}
                containerStyle={styles.icon}
                size={theme.iconSize.small}
                color={hasBackground ? theme.colors.white : theme.colors.primary}
              />
              <Text type="body" color={hasBackground ? theme.colors.white : theme.colors.primary}>
                {title}
              </Text>
              {isMultiple && (
                <Icon name="redRadio" containerStyle={styles.icon} size={theme.iconSize.small} />
              )}
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
    </BottomModalBase>
  )
}

const getStyle = (theme: Theme, fullScreen?: boolean) =>
  StyleSheet.create({
    modal: {
      justifyContent: 'flex-end',
      margin: 0,
      ...(fullScreen && { flex: 1 }),
    },
    modalDesktop: {
      justifyContent: 'center',
      alignItems: 'center',
      margin: 0,
    },
    closeButton: {
      position: 'absolute',
      top: theme.spacing.lg,
      right: theme.spacing.lg,
      padding: theme.spacing.xs,
      zIndex: 10,
    },
    container: {
      flex: 1,
      width: '100%',
      backgroundColor: theme.colors.white,
      alignItems: 'center',
      paddingHorizontal: theme.spacing.lg,
      borderTopLeftRadius: theme.border.jumbo,
      borderTopRightRadius: theme.border.jumbo,
    },
    containerFullScreen: {
      flex: 1,
      minHeight: '100%',
    },
    wrapContentWeb: {
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: 'auto',
    },
    containerNonMobile: {
      minWidth: 550,
      backgroundColor: theme.colors.white,
      alignItems: 'center',
      paddingHorizontal: theme.spacing.lg,
      borderRadius: theme.border.jumbo,
      paddingBottom: theme.spacing.xxl * 2,
      shadowColor: theme.colors.black,
      shadowOffset: {
        width: 0,
        height: 4,
      },
      shadowOpacity: 0.3,
      shadowRadius: 8,
      elevation: 8,
    },
    container2: {
      width: '100%',
      gap: 16,
    },
    options: {
      width: '100%',
    },
    optionsContent: {
      alignItems: 'center',
      gap: theme.spacing.sm,
    },
    indicator: {
      position: 'absolute',
      top: theme.spacing.md,
      alignSelf: 'center',
      width: 50,
      height: 8,
      borderRadius: theme.border.extraSmall,
      backgroundColor: theme.colors.secondary,
    },
    option: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      borderColor: theme.colors.primary,
      borderWidth: 1,
      paddingVertical: theme.spacing.md,
      borderRadius: theme.spacing.xxxl,
    },
    icon: {
      paddingRight: theme.spacing.md,
    },
  })
